var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createAction } from '@reduxjs/toolkit';
/**
 * Creates and manages three redux actions for asynchronous operations. These
 * are `pending`, `fulfilled`, and `rejected`. The actions can either be
 * dispatched one-by-one for fine grained control. Or the thunk (bare function)
 * can be dispatched and awaited, which in turn will dispatch `pending` and
 * `fulfilled`/`rejected` when the promise executor functions in the meta are
 * invoked.
 *
 * @example
 *   ```
 *   // Declaration
 *   const getFoo = createAsyncThunkAction<number, string, boolean>('app/getFoo');
 *
 *   // Consumption (e.g. in component)
 *   const name = await dispatch(getFoo(42)); // Returns 'Bob'
 *
 *   // Consumption (e.g. in middleware)
 *   action.meta.resolve('Bob'); // Dispatches fulfilled
 *   action.meta.reject(false); // Dispatches rejected
 *   ```;
 *
 * @example
 *   ```
 *   // Declaration
 *   const getFoo = createAsyncThunkAction<number, string, boolean>('app/getFoo');
 *
 *   // Consumption
 *   dispatch(getFoo.pending(41));
 *   dispatch(getFoo.fulfilled('Alice'));
 *   dispatch(getFoo.rejected(false));
 *   ```;
 *
 * @template TPending The pending payload.
 * @template TFulfilled The fulfilled payload.
 * @template TRejected The rejected payload.
 * @param typePrefix The action prefix.
 */
export function createAsyncThunkAction(typePrefix) {
    const pending = createAction(`${typePrefix}/pending`, (payload) => ({
        meta: { requestStatus: 'pending' },
        payload,
    }));
    const fulfilled = createAction(`${typePrefix}/fulfilled`, (payload) => ({
        meta: { requestStatus: 'fulfilled' },
        payload,
    }));
    const rejected = createAction(`${typePrefix}/rejected`, (payload) => ({
        meta: { requestStatus: 'rejected' },
        payload,
    }));
    const thunk = createAction(`${typePrefix}/pending`, (payload, resolve, reject) => ({
        meta: { requestStatus: 'pending', resolve, reject },
        payload,
    }));
    return Object.assign((payload) => {
        return (dispatch) => __awaiter(this, void 0, void 0, function* () {
            try {
                const result = yield new Promise((resolve, reject) => {
                    dispatch(thunk(payload, resolve, reject));
                });
                return dispatch(fulfilled(result));
            }
            catch (error) {
                return dispatch(rejected(error));
            }
        });
    }, {
        thunk,
        pending,
        fulfilled,
        rejected,
    });
}
